/**
 * Site-wide product/feature catalogue: menu grouping, icons, one-line
 * descriptions and the "What is it / How it works" copy used by the
 * navbar, footer, homepage, /features and every product detail page.
 * Long-form page content (features, FAQs…) stays in data/products.js.
 */
import {
  MessagesSquare, Megaphone, Send, Bot, Inbox, Users, PhoneCall, ShoppingBag, BadgeCheck, QrCode,
  Repeat, Droplets, Target, MousePointerClick, Star, Share2, Sparkles, Link2, Cable, Code2, Sheet,
  Store, Webhook, Facebook, Instagram,
} from "lucide-react";
import { PRODUCTS } from "@/data/products";

export const CATALOG = {
  // ---------------------------------------------------------------- Products
  "whatsapp-business-api": {
    group: "products", icon: MessagesSquare,
    short: "Official API access with verified onboarding.",
    whatIs: "The WhatsApp Business API (Cloud API) is Meta's official way for businesses to message customers at scale — with a verified business profile, approved message templates, multiple agents on one number and full automation. GrabNChat is a Meta Tech Provider, so you get the API with a no-code dashboard on top.",
    steps: [
      ["Sign up & connect", "Connect your Facebook Business account and phone number through Meta's Embedded Signup in a few clicks."],
      ["Verify & set up", "Complete business verification, set your display name and profile, and submit your first templates."],
      ["Go live", "Start broadcasting, automate replies and let your whole team chat from one number."],
    ],
  },
  "bulk-whatsapp-marketing": {
    group: "products", icon: Megaphone,
    short: "Personalised campaigns to thousands of customers.",
    whatIs: "Bulk WhatsApp marketing lets you send approved promotional messages — offers, launches, reminders — to your opted-in customers at once, each personalised with their name and details, and then track who received, read, clicked and replied.",
    steps: [
      ["Import contacts", "Upload a CSV or sync contacts from your store, CRM, Google Sheets or lead forms."],
      ["Create a template", "Design a rich message with images, video, buttons and variables and submit it for approval."],
      ["Send & track", "Send now or schedule, then watch delivery, read and reply rates in real time."],
    ],
  },
  "whatsapp-broadcast": {
    group: "products", icon: Send,
    short: "Schedule and send broadcasts with live analytics.",
    whatIs: "WhatsApp broadcasts deliver one approved message to a segment of your contacts in a single send — ideal for announcements, sales and updates — with scheduling and per-campaign reporting.",
    steps: [
      ["Pick an audience", "Choose contacts by tag or attribute, or upload a fresh list."],
      ["Choose a template", "Select an approved template and fill in its variables."],
      ["Schedule & measure", "Send immediately or at a set time and track results per campaign."],
    ],
  },
  "whatsapp-chatbot": {
    group: "products", icon: Bot,
    short: "Drag-and-drop bots for 24/7 replies.",
    whatIs: "A WhatsApp chatbot answers customers automatically — FAQs, lead capture, bookings, order status — using flows you design visually, and hands the chat to a human agent whenever needed.",
    steps: [
      ["Design the flow", "Drag and drop messages, buttons, lists, questions and conditions on the flow builder."],
      ["Set triggers", "Start flows from keywords, ad clicks, QR codes or the first message of a chat."],
      ["Publish & improve", "Go live instantly and refine the flow based on real conversations."],
    ],
  },
  "shared-team-inbox": {
    group: "products", icon: Inbox,
    short: "One inbox for your whole team — WhatsApp, Instagram & Messenger.",
    whatIs: "The shared team inbox lets every agent reply from the same business number. WhatsApp, Instagram and Messenger chats arrive in one place with assignment, tags, notes and chat history, so no customer is missed or answered twice.",
    steps: [
      ["Invite your team", "Add agents and choose what each of them can see and do."],
      ["Route chats", "Assign conversations manually or automatically and tag them by topic."],
      ["Reply together", "Chat, leave internal notes and hand over conversations without losing context."],
    ],
  },
  "whatsapp-crm": {
    group: "products", icon: Users,
    short: "Contacts, tags, attributes and full chat history.",
    whatIs: "The WhatsApp CRM keeps every customer's details, tags, custom attributes and complete conversation history in one profile, so your team knows the context before replying and campaigns reach the right people.",
    steps: [
      ["Bring in contacts", "Import lists or capture contacts automatically from chats, ads and forms."],
      ["Organise", "Add tags and custom attributes like city, plan or order value."],
      ["Act on it", "Segment for broadcasts, trigger automations and follow up from the profile."],
    ],
  },
  "whatsapp-calling": {
    group: "products", icon: PhoneCall,
    short: "Answer WhatsApp voice calls in the browser.",
    whatIs: "WhatsApp Business Calling lets customers call your business number over WhatsApp and lets your agents answer — and call back — right in the GrabNChat dashboard, with every call logged against the customer.",
    steps: [
      ["Enable calling", "Turn on calling for your number and choose when and where customers can call."],
      ["Answer in the browser", "Incoming calls ring in the dashboard; agents pick up with a headset."],
      ["Review", "See every call in the Calls list and push call events to your systems via webhooks."],
    ],
  },
  "whatsapp-catalog-ecommerce": {
    group: "products", icon: ShoppingBag,
    short: "Catalogs, products in chat and a Shopify app.",
    whatIs: "WhatsApp Commerce lets customers browse your products and add them to cart without leaving the chat. GrabNChat manages your Meta catalogs and, with the Shopify app, sends order and shipping updates automatically.",
    steps: [
      ["Create a catalog", "Add products with photos and prices, or import an existing Meta catalog."],
      ["Share in chat", "Send single products or product lists to customers and from chatbots."],
      ["Automate orders", "Connect Shopify to send confirmations and shipping updates on WhatsApp."],
    ],
  },
  "green-tick-verification": {
    group: "products", icon: BadgeCheck,
    short: "Help getting the official verified badge.",
    whatIs: "The WhatsApp verified badge shows customers your business is authentic. GrabNChat guides you through Meta's requirements, prepares your application and follows up until a decision is made.",
    steps: [
      ["Eligibility check", "We review your brand presence and business verification status."],
      ["Application", "We prepare and submit the request with the right supporting information."],
      ["Follow-up", "We track the request with Meta and advise on next steps."],
    ],
  },
  "qr-kits": {
    group: "products", icon: QrCode,
    short: "Print-ready QR posters for chats and Google reviews.",
    whatIs: "QR Kits are printable posters for your counter, tables, door or bills. Customers scan to start a WhatsApp chat with you or to rate you in 10 seconds, and every lead and rating lands in your GrabNChat app.",
    steps: [
      ["Connect", "Connect WhatsApp (and Google Business for the Rate Us kit)."],
      ["Design", "Choose a kit and style and edit the headline in the app."],
      ["Print", "Download the print-ready PNG and place it where customers pay or wait."],
    ],
  },

  // ---------------------------------------------------------------- Features
  "whatsapp-automation": {
    group: "features", icon: Repeat,
    short: "Keyword replies, journeys and automated follow-ups.",
    whatIs: "WhatsApp automation handles repetitive conversations for you: instant replies to keywords, welcome and away messages, follow-ups and event-based journeys that run on their own.",
    steps: [
      ["Choose a trigger", "A keyword, a tag, a new lead, an order or a time delay."],
      ["Add actions", "Send templates, run a chatbot flow, tag the contact or assign an agent."],
      ["Let it run", "Automations work 24/7 and you can pause or edit them any time."],
    ],
  },
  "drip-campaigns": {
    group: "features", icon: Droplets,
    short: "Timed follow-up sequences that stop on conversion.",
    whatIs: "A drip campaign sends a series of WhatsApp templates over days or weeks — welcome, nudge, reminder — and removes each contact automatically as soon as they convert.",
    steps: [
      ["Pick the audience", "All contacts or a specific tag."],
      ["Build the sequence", "Entry template, follow-up steps with delays, and an exit template."],
      ["Set timing", "Choose sending hours and days; the drip runs on its own."],
    ],
  },
  "lead-management": {
    group: "features", icon: Target,
    short: "Lead Ads, Sheets and WhatsApp leads in one list.",
    whatIs: "Lead management collects leads from Facebook and Instagram Lead Ads, Google Sheets, click-to-WhatsApp links and chats into one list, so your team can follow up on WhatsApp within minutes.",
    steps: [
      ["Connect sources", "Link your Lead Ads forms and Google Sheets."],
      ["Capture automatically", "New leads arrive in real time; older ones can be synced."],
      ["Follow up", "Message leads on WhatsApp or add them to a drip campaign."],
    ],
  },
  "ads-manager": {
    group: "features", icon: MousePointerClick,
    short: "Click-to-WhatsApp ads and one-click post boosts.",
    whatIs: "Ads Manager lets you create Meta campaigns and boost Facebook posts from GrabNChat, including ads that open a WhatsApp chat — so ad clicks become conversations your team can close.",
    steps: [
      ["Connect your ad account", "Link the Meta ad account and Page you already use."],
      ["Create or boost", "Build a campaign or boost an existing post with a budget and audience."],
      ["Launch safely", "New ads start paused; switch them on when you're ready."],
    ],
  },
  "google-business-profile": {
    group: "features", icon: Star,
    short: "Profile, posts, reviews and a rating link.",
    whatIs: "Google Business Profile management lets you keep your Google listing up to date, publish posts and photos, reply to reviews and collect more ratings with a short link — all from GrabNChat.",
    steps: [
      ["Connect Google", "Sign in with the Google account that manages your profile and pick your location."],
      ["Manage", "Edit your profile, post updates and reply to reviews."],
      ["Grow ratings", "Share your rating link on WhatsApp or print it as a QR Kit."],
    ],
  },
  "facebook-instagram-management": {
    group: "features", icon: Share2,
    short: "Posts, comments, DMs and insights for Facebook & Instagram.",
    whatIs: "Facebook & Instagram management brings your Page and Instagram account into GrabNChat: moderate comments, answer DMs from the shared inbox and track insights without switching apps.",
    steps: [
      ["Connect accounts", "Connect your Facebook Page and Instagram professional account."],
      ["Moderate & reply", "Reply to, hide or delete comments and answer DMs in the inbox."],
      ["Measure", "Track reach, engagement and follower growth."],
    ],
  },
  "ai-content-studio": {
    group: "features", icon: Sparkles,
    short: "AI images, captions and short videos for social.",
    whatIs: "The AI content studio creates social media images, captions and short videos from a simple description, adds your logo, and publishes or schedules them to Facebook and Instagram.",
    steps: [
      ["Describe it", "Type what you want to post and choose a size."],
      ["Generate & edit", "Get images, captions or a short video and tweak them."],
      ["Publish", "Post now or schedule to Facebook and Instagram."],
    ],
  },
};

const entry = (slug) => ({ slug, title: PRODUCTS[slug]?.title || slug, ...CATALOG[slug] });

export const PRODUCT_MENU = Object.keys(CATALOG).filter((s) => CATALOG[s].group === "products" && PRODUCTS[s]).map(entry);
export const FEATURE_MENU = Object.keys(CATALOG).filter((s) => CATALOG[s].group === "features" && PRODUCTS[s]).map(entry);
export const ALL_OFFERINGS = [...PRODUCT_MENU, ...FEATURE_MENU];
export const getOffering = (slug) => (CATALOG[slug] ? entry(slug) : null);

/** Integrations shown on /integrations and in the Integrations menu. */
export const INTEGRATIONS = [
  { slug: "shopify", name: "Shopify", icon: Store, category: "E-commerce", desc: "Send order confirmations, shipping updates and other store notifications on WhatsApp with the GrabNChat Shopify app.", link: "/products/whatsapp-catalog-ecommerce" },
  { slug: "google-sheets", name: "Google Sheets", icon: Sheet, category: "Leads", desc: "Import leads from any Google Sheet with read-only access — just paste the sheet link.", link: "/products/lead-management" },
  { slug: "facebook-lead-ads", name: "Facebook & Instagram Lead Ads", icon: Facebook, category: "Leads", desc: "Receive Instant Form leads in real time and sync older submissions.", link: "/products/lead-management" },
  { slug: "google-business-profile", name: "Google Business Profile", icon: Star, category: "Reviews", desc: "Manage your profile, posts and reviews and collect ratings with a short link.", link: "/products/google-business-profile" },
  { slug: "instagram", name: "Instagram", icon: Instagram, category: "Channels", desc: "Answer Instagram DMs in the shared inbox and moderate comments.", link: "/products/facebook-instagram-management" },
  { slug: "messenger", name: "Facebook Messenger", icon: MessagesSquare, category: "Channels", desc: "Handle Messenger chats alongside WhatsApp and send permitted Messenger broadcasts.", link: "/products/facebook-instagram-management" },
  { slug: "meta-ads", name: "Meta Ads", icon: MousePointerClick, category: "Ads", desc: "Create campaigns, boost posts and run click-to-WhatsApp ads.", link: "/products/ads-manager" },
  { slug: "rest-api", name: "REST API", icon: Code2, category: "Developers", desc: "Send messages and templates and manage contacts from your own systems.", link: "/docs" },
  { slug: "webhooks", name: "Webhooks", icon: Webhook, category: "Developers", desc: "Receive message, call and lead events in your CRM or backend in real time.", link: "/docs#realtime" },
];

/** Free tools (Resources menu + footer). */
export const FREE_TOOLS = [
  { to: "/tools/whatsapp-link-generator", label: "WhatsApp Link Generator", icon: Link2 },
  { to: "/products/qr-kits#try-it", label: "QR Poster Builder", icon: QrCode },
];

export const INTEGRATION_ICON = Cable;
